/**
 * Blog index: SEO guides about CVs and ATS, filterable by category.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Seo, { SITE_URL } from '../components/Seo';
import {
  getAllPosts,
  getLocalizedPost,
  CATEGORY_LABELS,
} from '../content/blog/posts';
import './Blog.css';

const BlogList = () => {
  const { lang, t, localePath } = useLanguage();
  const [category, setCategory] = useState('all');
  const labels = CATEGORY_LABELS[lang === 'en' ? 'en' : 'es'];
  const allPosts = useMemo(
    () => getAllPosts().map((p) => getLocalizedPost(p, lang)),
    [lang]
  );
  const categories = useMemo(() => {
    const set = new Set(getAllPosts().map((p) => p.category).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, []);
  const posts = useMemo(
    () => (category === 'all' ? allPosts : allPosts.filter((p) => p.category === category)),
    [allPosts, category]
  );

  const itemListLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: t('blog.indexTitle'),
    description: t('blog.indexDescription'),
    url: `${SITE_URL}${localePath('/blog')}`,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: allPosts.length,
      itemListElement: allPosts.map((post, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `${SITE_URL}${localePath(`/blog/${post.slug}`)}`,
        name: post.title,
      })),
    },
  };

  return (
    <div className="blog-page">
      <Seo
        title={t('blog.indexTitle')}
        description={t('blog.indexDescription')}
        path="/blog"
        lang={lang}
        hreflang
        keywords="CV, ATS, currículum, plantillas, ClarCV"
        jsonLd={itemListLd}
      />
      <div className="container">
        <header className="blog-page__header">
          <h1>{t('blog.indexTitle')}</h1>
          <p>{t('blog.indexLead')}</p>
          <p className="blog-page__count">
            {allPosts.length} {t('blog.guidesCount')}
          </p>
        </header>
        <div className="blog-filters" role="tablist" aria-label={t('blog.filterLabel')}>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={category === cat}
              className={`blog-filter${category === cat ? ' is-active' : ''}`}
              onClick={() => setCategory(cat)}
            >
              {cat === 'all' ? t('blog.filterAll') : labels[cat] || cat}
            </button>
          ))}
        </div>
        <ul className="blog-list">
          {posts.map((post) => (
            <li key={post.slug}>
              <Link to={localePath(`/blog/${post.slug}`)} className="blog-list__item">
                <div className="blog-list__meta">
                  <span className="blog-chip">{labels[post.category] || post.category}</span>
                  {post.date} · {post.readMinutes} {t('blog.minRead')}
                </div>
                <div className="blog-list__title">{post.title}</div>
                <p className="blog-list__desc">{post.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default BlogList;
